<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import { Link } from 'lucide-vue-next'
import ElementalMenu from '~~/src/components/Elemental/Options/index.vue'
import { useElement } from '@/components/Elemental/context/context'
import { ElementalTypeEnum, toList } from '@/types/elemental'
import { useTopicsQuery } from '~~/src/composables/query/topic/useTopicsQuery'
import ElementalIconPicker from '@/components/Elemental/ElemetalIconPicker/index.vue'
import SaveOnList from '@/components/Elemental/SaveOnList/SaveElementalInAList.vue'
import ElementalUpvote from '@/components/Elemental/Upvote/index.vue'
import { Button } from '@/components/ui/button'
import { useSidebarStore } from '~~/src/stores/sidebar'

const props = defineProps<{
  hideInteractionBar?: boolean
  hideElementalMenu?: boolean
  isInLibrary?: boolean
  showTitle?: boolean
}>()

defineOptions({
  name: 'ProductHeader',
})

const { data: topics } = useTopicsQuery({ query: {} })
const { elemental, elementalType } = useElement()
const { isSmall } = useContainerSize()
const { back } = useRouter()
const { goToPrevious } = useSidebarStore()
const { isSmaller } = useBreakpoints(breakpointsTailwind)

const { copy } = useClipboard()
const toast = useToast()

const isMobile = ref(isSmaller('md'))
const showExpansionToggler = ref(false)
const croppedContent = ref<string>('')

whenever(elemental, () => {
  if (!elemental.value)
    return

  if (elemental.value.description) {
    const { content, hasReachedLimit } = cropDescriptionContent(elemental.value.description, 120)
    croppedContent.value = content
    showExpansionToggler.value = hasReachedLimit
  }
})

const copyLink = () => {
  const link = `${getPromptLocation()}${getPromptLocation().includes('?') ? '&' : '?'}header=false&sidebar_left=false&sidebar_right=false&other_images=false`
  copy(link)
  toast.success('Link copied to clipboard')
}

const topicsMap = computed(() => {
  const map = new Map<number, { name: string; url: string }>()
  topics.value?.forEach(topic => map.set(topic.id, topic))
  return map
})

const elementalTypeLink = computed(() => {
  if (!elementalType.value?.name)
    return undefined
  if (['prompt', 'automation'].includes(elementalType.value?.name))
    return `/${elementalType.value?.name}s`
  if (['list'].includes(elementalType.value?.name))
    return '/'
  return undefined
})

const typeName = computed(() => {
  const elementalTypeName = elementalType.value?.name || ''

  return elementalType.value?.id !== ElementalTypeEnum.List
    ? capitalizeFirstLetter(elementalTypeName)
    : 'List'
})

const headerClasses = computed(() => ({
  'p-2': !props.isInLibrary,
  'gap-1': props.isInLibrary,
  'gap-2': !props.isInLibrary && isSmall,
  'gap-6': !props.isInLibrary && !isSmall,
  'border-b-1 border-softGray': !props.isInLibrary,
}))

const router = useRouter()

const handleButtonClick = () => {
  if (props.isInLibrary) {
    goToPrevious(isMobile.value)
    return
  }

  const from = window.history.state.back
  if (!from) {
    router.push(`/${elemental.value?.user.username}`)
    return
  }

  back()
}
</script>

<template>
  <header class="flex w-full items-center gap-2 sticky top-0 bg-white z-40" :class="headerClasses">
    <Button
      variant="snack-ghost"
      size="icon"
      class="rounded-full shrink-0"
      @click="handleButtonClick"
    >
      <div class="i-material-symbols:chevron-left w-7 h-7" />
    </Button>

    <slot name="title">
      <TextV2 class="text-dark-gray flex flex-wrap items-center body-14 gap-2">
        <NuxtLink :to="elementalTypeLink">
          <div class="flex items-center gap-1 border-solid border-1 text-center rounded px-[10px] flex-shrink-0 whitespace-nowrap">
            <ElementalIconPicker :type="elemental?.elementalTypeId === 0 ? (elemental.contentTypeId ?? ElementalTypeEnum.List) : elemental?.elementalTypeId! " class="w-4 h-4" />
            <span>{{ typeName }}</span>
          </div>
        </NuxtLink>
        <h1 v-if="showTitle" class="text-darkGray font-700 font-inter text-sm leading-[160%] hidden md:visible md:block">
          {{ elemental?.title }}
        </h1>

        <!-- Topics list -->
        <template
          v-if="getElementalSection(elementalType, 'topic_ids') && topics?.length && elemental?.topicIds?.length && !isSmall"
        >
          <span
            v-for="(topicId, index) in elemental?.topicIds?.slice(0, 3)"
            :key="topicId"
          >
            <NuxtLink :to="`/topic/${topicsMap.get(topicId)?.url}`" @click.stop>
              {{ topicsMap.get(topicId)?.name }}
            </NuxtLink>{{ index < 2 && index < ((elemental?.topicIds?.length || 0) - 1) ? ', ' : '' }}
          </span>
        </template>
      </TextV2>
    </slot>

    <div class="flex items-center justify-between ml-a justify-self-end empty:hidden">
      <ClientOnly>
        <template v-if="elementalType?.name === 'list'">
          <AppAdminList v-if="elemental" :list="elemental && toList(elemental)" />
        </template>
        <template v-else>
          <AppAdminElemental v-if="elemental" :elemental="elemental" :elemental-type="elementalType" />
        </template>
      </ClientOnly>

      <!-- Área de interação com toggle -->
      <div v-if="!hideInteractionBar" class="flex  items-center interaction-bar">
        <ElementalUpvote v-if="elemental" :element="elemental" class="mr-1" />
        <SaveOnList v-if="elemental" :elemental="elemental" :elemental-type="elementalType?.id" variant="default-elemental" />
        <div>
          <Button
            v-if="elementalType?.id !== ElementalTypeEnum.Output && elementalType?.id !== ElementalTypeEnum.Snippet"
            variant="snack-icon"
            size="icon"
            @click="copyLink()"
          >
            <Link />
          </Button>
        </div>
      </div>
      <div v-if="elemental && !hideElementalMenu" class="flex gap-2 items-center">
        <ElementalMenu :elemental="elemental" />
      </div>
    </div>
  </header>
</template>
